import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Save, ShieldCheck, Globe, Copy, Check, Trash2, AlertTriangle, Info, LogIn } from "lucide-react";
import ApiClient from "../utils/api";
import { useToast } from "../context/ToastContext";
import DeleteConfirmModal from "../components/DeleteConfirmModal";

const UNCHANGED_KEY = "__UNCHANGED__";
const MASK_DISPLAY = "••••••••••••••••••••";

const formatDate = (unix) =>
  unix ? new Date(unix * 1000).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "";

const InstagramConfig = ({ activeAiAgentId }) => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [config, setConfig] = useState(null);           // as saved on the server
  const [platform, setPlatform] = useState({ ready: true, mode: "relay", webhook_url: "", login: false });
  const [showTokenField, setShowTokenField] = useState(false);
  const [signingIn, setSigningIn] = useState(false);
  const returnHandled = useRef(false);
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      const response = await ApiClient.request("/instagram/config", { aiAgentId: activeAiAgentId });
      if (response.success) {
        setConfig(response.config);
        setToken(response.config?.access_token ? UNCHANGED_KEY : "");
        if (response.platform) setPlatform(response.platform);
      }
    } catch (error) {
      console.log("Instagram config not found");
    }
  }, [activeAiAgentId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // back from Instagram's login (through EasyChat Control): the code is in the URL fragment, never sent to a server
  useEffect(() => {
    if (returnHandled.current) return;
    const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const code = params.get("ig_code");
    const error = params.get("ig_error");
    if (!code && !error) return;
    returnHandled.current = true;
    window.history.replaceState(null, "", window.location.pathname);
    if (error) {
      showToast("Instagram sign-in was cancelled", "warning");
      return;
    }
    (async () => {
      setSigningIn(true);
      try {
        const response = await ApiClient.request("/instagram/oauth-complete", {
          aiAgentId: activeAiAgentId,
          method: "POST",
          body: JSON.stringify({ code, state: params.get("ig_state") || "" }),
        });
        if (response.success) showToast(response.message || "Instagram connected", "success");
      } catch (e) {
        // Error handled by ApiClient global toast
      } finally {
        setSigningIn(false);
        loadConfig();
      }
    })();
  }, [activeAiAgentId, loadConfig, showToast]);

  const connectWithInstagram = async () => {
    try {
      setSigningIn(true);
      const response = await ApiClient.request("/instagram/login-url", { aiAgentId: activeAiAgentId });
      if (response.url) {
        window.location.assign(response.url);
        return;
      }
    } catch (e) {
      // Error handled by ApiClient global toast
    }
    setSigningIn(false);
  };

  // an account connected on Integrations -> Instagram (own Meta app) is managed there
  const ownApp = config?.setup === "manual" && Boolean(config?.ig_user_id);
  const connected = Boolean(config?.ig_user_id) && !ownApp;
  const disconnected = connected && config?.connection_status === "disconnected";
  const relayFailed = connected && config?.relay_status === "failed";

  const saveConfig = async () => {
    if (!token.trim() || token === UNCHANGED_KEY) {
      showToast("Please paste the Instagram access token", "error");
      return;
    }
    try {
      setSaving(true);
      const response = await ApiClient.request("/instagram/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({ access_token: token.trim() }),
      });
      if (response.success) {
        showToast(response.message || "Instagram connected", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    } finally {
      setSaving(false);
      loadConfig();
    }
  };

  const toggleActive = async () => {
    if (!connected) {
      showToast("Please connect an Instagram account first", "error");
      return;
    }
    try {
      const response = await ApiClient.request("/instagram/config", {
        aiAgentId: activeAiAgentId,
        method: "PUT",
        body: JSON.stringify({ is_active: !config.is_active }),
      });
      if (response.success) {
        setConfig(response.config);
        showToast(response.active ? "Instagram activated" : "Instagram deactivated", "success");
      }
    } catch (error) {
      // Error handled by ApiClient global toast
    }
  };

  const deleteConfig = async () => {
    try {
      setSaving(true);
      const response = await ApiClient.request("/ai-agents/integrations/instagram", { method: "DELETE" });
      if (response.success) {
        setConfig(null);
        setToken("");
        showToast("Instagram disconnected", "success");
      }
    } catch (error) {
      // handled globally
    } finally {
      setSaving(false);
      setIsDeleteModalOpen(false);
    }
  };

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(platform.webhook_url);
      setCopied(true);
      showToast("Webhook URL copied to clipboard!", "success");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      showToast("Failed to copy URL", "error");
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-4 sm:space-y-6">

        {/* Top Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-4 sm:pb-5 border-b border-zinc-100 gap-3 sm:gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/integrations")}
              className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded-lg transition-colors"
              title="Back to Integrations"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">Instagram Integration</h1>
              <p className="text-xs text-zinc-500 mt-0.5">
                Answer your Instagram direct messages with this AI agent.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 self-start sm:self-auto pl-9 sm:pl-0">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold tracking-wide uppercase border ${
              (config?.is_active && !ownApp) && !disconnected
                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                : "bg-zinc-50 text-zinc-500 border-zinc-200"
            }`}>
              {(config?.is_active && !ownApp) ? (disconnected ? "Disconnected" : "Active") : "Disabled"}
            </span>
            <button
              type="button"
              onClick={toggleActive}
              className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                (config?.is_active && !ownApp) ? "bg-zinc-900" : "bg-zinc-200"
              }`}
              title={(config?.is_active && !ownApp) ? "Deactivate integration" : "Activate integration"}
            >
              <div
                className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                  (config?.is_active && !ownApp) ? "left-4" : "left-0.5"
                }`}
              />
            </button>
          </div>
        </div>

        {ownApp && (
          <div className="flex items-start gap-2.5 p-3.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-800">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <p className="text-xs">
              This AI agent uses Instagram with its own Meta app (@{config.username || config.ig_user_id}). Manage it under
              Integrations → Instagram (own Meta app), or disconnect it there to use Connect with Instagram.
            </p>
          </div>
        )}
        {!platform.ready && (
          <div className="flex items-start gap-2.5 p-3.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-800">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <p className="text-xs">Instagram is not set up on this server yet. Please contact support.</p>
          </div>
        )}
        {disconnected && (
          <div className="flex items-start gap-2.5 p-3.5 rounded-lg border border-red-200 bg-red-50 text-red-700">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <p className="text-xs">
              Instagram is not connected: {config.connection_error || "access was removed"}. Customers get no replies
              until you paste a new access token below.
            </p>
          </div>
        )}
        {relayFailed && !disconnected && (
          <div className="flex items-start gap-2.5 p-3.5 rounded-lg border border-red-200 bg-red-50 text-red-700">
            <AlertTriangle size={15} className="shrink-0 mt-0.5" />
            <p className="text-xs">
              Messages cannot reach this AI agent yet: {config.relay_error || "routing failed"}. Paste the token again and
              save to retry.
            </p>
          </div>
        )}

        {/* Account Card */}
        <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-5">
          <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
            <ShieldCheck className="text-zinc-800 shrink-0" size={18} />
            <div>
              <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Instagram Account</h2>
              <p className="text-[11px] text-zinc-500">An Instagram professional account (business or creator)</p>
            </div>
          </div>

          {connected && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Account</p>
                <p className="text-xs font-semibold text-zinc-900 mt-1">@{config.username || "unknown"}</p>
                <p className="text-[10px] text-zinc-400 font-mono mt-0.5">{config.ig_user_id}</p>
              </div>
              <div className="p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Status</p>
                <p className={`text-xs font-semibold mt-1 ${disconnected || relayFailed ? "text-red-600" : "text-emerald-700"}`}>
                  {disconnected ? "Disconnected" : relayFailed ? "Not receiving messages" : "Connected"}
                </p>
              </div>
              <div className="p-3 bg-zinc-50 rounded-lg border border-zinc-200">
                <p className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wider">Access token</p>
                <p className="text-xs font-semibold text-zinc-900 mt-1">Renewed automatically</p>
                {config.token_expires_at && (
                  <p className="text-[10px] text-zinc-400 mt-0.5">valid until {formatDate(config.token_expires_at)}</p>
                )}
              </div>
            </div>
          )}

          {platform.login && (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3.5 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="flex-1">
                <p className="text-xs font-medium text-zinc-900">
                  {connected ? "Connect another account or renew access" : "Sign in with the Instagram professional account"}
                </p>
                <p className="text-[11px] text-zinc-500 mt-0.5">
                  Opens Instagram's login. Allow messaging access; you come back here when it is done.
                </p>
              </div>
              <button
                type="button"
                onClick={connectWithInstagram}
                disabled={signingIn || saving || !platform.ready || ownApp}
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 text-xs font-medium transition-colors"
              >
                {signingIn ? (
                  <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <LogIn size={14} />
                )}
                <span>{signingIn ? "Connecting..." : "Connect with Instagram"}</span>
              </button>
            </div>
          )}

          {platform.login && !showTokenField && (
            <button
              type="button"
              onClick={() => setShowTokenField(true)}
              className="text-[11px] text-zinc-500 underline hover:text-zinc-800"
            >
              Use an access token instead
            </button>
          )}

          {(!platform.login || showTokenField) && (
          <div>
            <label className="block text-xs font-medium text-zinc-700 mb-1.5">
              {connected ? "Replace access token" : "Instagram access token"}
            </label>
            <input
              type="password"
              value={token === UNCHANGED_KEY ? MASK_DISPLAY : token}
              onChange={(e) => setToken(e.target.value)}
              onFocus={() => {
                if (token === UNCHANGED_KEY) setToken("");
              }}
              placeholder={token === UNCHANGED_KEY ? MASK_DISPLAY : "IGAA..."}
              className="w-full px-3.5 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 font-mono transition-colors"
            />
            <p className="text-[11px] text-zinc-400 mt-1">
              The long-lived token of the professional account (Meta app → Instagram → API setup with Instagram login →
              Generate access tokens). It is checked with Instagram, stored encrypted and renewed automatically.
            </p>
          </div>
          )}

          <div className="flex items-start gap-2 p-3 bg-zinc-50 rounded-lg border border-zinc-200">
            <Info size={13} className="text-zinc-400 shrink-0 mt-0.5" />
            <p className="text-[11px] text-zinc-500">
              The AI agent answers text messages. Images, voice messages and stickers are not answered. When an agent
              takes the chat in Live Chat, the AI stays quiet and the agent's replies are sent to Instagram; Instagram
              only allows replies within 24 hours of the customer's last message.
            </p>
          </div>

          <div className="pt-3 border-t border-zinc-100 flex flex-col-reverse sm:flex-row sm:items-center justify-between gap-2.5">
            {connected ? (
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(true)}
                disabled={saving}
                className="inline-flex items-center justify-center gap-1.5 px-3.5 py-2 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors disabled:opacity-40"
              >
                <Trash2 size={14} />
                <span>Disconnect Instagram</span>
              </button>
            ) : <div />}

            {(!platform.login || showTokenField) ? (
            <button
              type="button"
              onClick={saveConfig}
              disabled={saving || !platform.ready || ownApp || !token.trim() || token === UNCHANGED_KEY}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 text-xs font-medium transition-colors"
            >
              {saving ? (
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Save size={14} />
              )}
              <span>{saving ? "Connecting..." : connected ? "Save token" : "Connect Instagram"}</span>
            </button>
            ) : <div />}
          </div>
        </div>

        {/* Direct mode only: the address for the Meta app's Webhooks settings */}
        {platform.mode === "direct" && platform.webhook_url && (
          <div className="bg-white rounded-xl border border-zinc-200 p-4 sm:p-6 space-y-3">
            <div className="flex items-center gap-2.5 pb-3 border-b border-zinc-100">
              <Globe className="text-zinc-800 shrink-0" size={18} />
              <div>
                <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Webhook URL</h3>
                <p className="text-[11px] text-zinc-500">Callback URL for the Meta app's Instagram webhooks (field: messages)</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-1.5 sm:p-2 bg-zinc-50 rounded-lg border border-zinc-200">
              <div className="font-mono text-[11px] sm:text-xs text-zinc-700 px-2 py-1 flex-1 break-all select-all min-w-0">
                {platform.webhook_url}
              </div>
              <button
                type="button"
                onClick={copyWebhookUrl}
                className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-shrink-0 ${
                  copied
                    ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                    : "bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-100"
                }`}
              >
                {copied ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                <span>{copied ? "Copied" : "Copy URL"}</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deleteConfig}
        title="Disconnect Instagram?"
        message="The AI agent stops answering this Instagram account's direct messages. You can connect it again with a token."
        confirmText="Disconnect"
        isLoading={saving}
      />
    </div>
  );
};

export default InstagramConfig;
